/* =========================================================
   TITAN SIGNS — Shopify configurator engine
   Quantity = number of signs, config rides as line item
   properties (incl. machine-readable `_tc`), and the
   titan-pricing-engine cart transform function recomputes the
   per-sign price server-side from the same rate table.

   TIERED PRICING: rate arrays index by tier; tierBy "sheets"
   (production sheets via nesting) or "sqft" (total billable).
   Tier logic MUST stay identical to the function's run.js.

   Canonical rounding rule (must match the function's run.js):
     totalCents = Math.round(total * 100)
     unitCents  = Math.ceil(totalCents / qty)
   ========================================================= */
(function () {
  "use strict";

  function init() {
    const rootEl = document.getElementById("titan-configurator");
    if (!rootEl || !window.TITAN_CONFIG) return;
    // guest mode: the section renders a sign-in panel instead of the
    // configurator, so there is nothing for the engine to drive
    if (!rootEl.querySelector("#tc-options")) return;
    const CFG = window.TITAN_CONFIG;
    const handle = rootEl.dataset.productHandle;
    const p = CFG.products[handle];
    if (!p) {
      rootEl.querySelector("#tc-options").innerHTML =
        "<p class='tc-warn'>No configurator data for product handle “" + handle + "”. Add it to titan-data.js.</p>";
      return;
    }

    const $ = (s) => rootEl.querySelector(s);
    const fmt = (n) => "$" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const has = (o) => p.options.includes(o);

    const matKeys = Object.keys(p.pricing.rates);
    const S = {
      w: p.defaultSize.w, h: p.defaultSize.h, qty: 10,
      material: matKeys[0], sides: "single",
      grommets: false, grommetPreset: "corners", grommetSpacing: 24,
      stakes: 0, gloss: false, contour: false, drillHoles: false,
      hem: true, rope: "none", polePocket: "none", polePocketSize: "2",
      windSlits: false, roundCorners: false,
      artDataUrl: null, artName: null, artUrl: null,
      buyMode: "sheet", // dual-mode products (aluminum): "sheet" | "custom"
    };
    const isSheetProduct = p.pricing.type === "sheet";
    const hasDualMode = !!(p.pricing.sheet && p.pricing.sheet.rates);
    const inSheetMode = () => isSheetProduct || (hasDualMode && S.buyMode === "sheet");

    /* Print-ready formats only — working files (.ai/.psd/.indd) break
       automated prepress. The browser can only PREVIEW raster formats. */
    const ART_ALLOWED = ["pdf", "tif", "tiff", "jpg", "jpeg", "png", "eps"];
    const ART_PREVIEWABLE = ["jpg", "jpeg", "png", "gif", "webp"];
    const ART_MAX_BYTES = 20 * 1024 * 1024;

    /* ---------- geometry ---------- */
    function nestFit(w, h) {
      const SW = p.pricing.sheetW, SH = p.pricing.sheetH;
      if (!SW || !w || !h) return null;
      const a = { cols: Math.floor(SW / w), rows: Math.floor(SH / h), rot: false };
      const b = { cols: Math.floor(SW / h), rows: Math.floor(SH / w), rot: true };
      a.fit = a.cols * a.rows; b.fit = b.cols * b.rows;
      return a.fit >= b.fit ? a : b;
    }
    function grommetPoints(w, h, preset, sp) {
      const L = 1, R = w - 1, T = 1, B = h - 1, pts = [];
      const add = (x1, y1, x2, y2) => {
        const len = Math.hypot(x2 - x1, y2 - y1), n = Math.max(1, Math.round(len / sp));
        for (let i = 0; i <= n; i++) pts.push([x1 + (x2 - x1) * i / n, y1 + (y2 - y1) * i / n]);
      };
      const pr = CFG.grommetPresets.find((g) => g.id === preset) || CFG.grommetPresets[0];
      if (pr.edges[0] === "corners") pts.push([L, T], [R, T], [L, B], [R, B]);
      else {
        if (pr.edges.includes("top")) add(L, T, R, T);
        if (pr.edges.includes("bottom")) add(L, B, R, B);
        if (pr.edges.includes("left")) add(L, T, L, B);
        if (pr.edges.includes("right")) add(R, T, R, B);
      }
      const seen = new Set();
      return pts.filter(([x, y]) => {
        const k = x.toFixed(1) + "," + y.toFixed(1);
        if (seen.has(k)) return false;
        seen.add(k); return true;
      });
    }

    /* ---------- tiered pricing (identical logic in run.js) ---------- */
    function tierIndexFor(w, h, qty, billableSqftTotal) {
      const pr = p.pricing;
      if (!pr.tiers || !pr.tierBy) return 0;
      let measure;
      if (pr.tierBy === "sheets") {
        const n = nestFit(w, h);
        const fit = n ? n.fit : 0;
        measure = fit > 0 ? Math.ceil(qty / fit) : qty;
      } else {
        measure = billableSqftTotal;
      }
      let idx = 0;
      for (let i = 0; i < pr.tiers.length; i++) if (measure >= pr.tiers[i]) idx = i;
      return idx;
    }
    const rateAt = (val, idx) => (Array.isArray(val) ? val[Math.min(idx, val.length - 1)] : val);

    /* ---------- pricing ---------- */
    function calc() {
      const lines = [], warnings = [];
      const rate = p.pricing.rates[S.material];
      /* billable area: some materials carry a per-sign minimum (matches S365) */
      const perSignSqft = Math.max((S.w * S.h) / 144, p.minSqftPerSign || 0);
      const areaT = perSignSqft * S.qty;
      const ti = tierIndexFor(S.w, S.h, S.qty, areaT);
      const tierName = p.pricing.tierNames && p.pricing.tiers && p.pricing.tiers.length > 1 ? p.pricing.tierNames[Math.min(ti, p.pricing.tierNames.length - 1)] : null;
      let base = 0;
      if (inSheetMode()) {
        /* whole-sheet commitment (S365 model): pay per 4x8 sheet consumed */
        const n = nestFit(S.w, S.h);
        const fit = n ? n.fit : 0;
        if (fit < 1) {
          warnings.push('This size doesn\'t nest on a ' + p.pricing.sheetW + '"×' + p.pricing.sheetH + '" sheet.');
        } else {
          const sheets = Math.ceil(S.qty / fit);
          let sIdx = 0;
          if (p.pricing.tiers && p.pricing.tierBy === "sheets") {
            for (let i = 0; i < p.pricing.tiers.length; i++) if (sheets >= p.pricing.tiers[i]) sIdx = i;
          }
          const src = isSheetProduct ? rate : p.pricing.sheet.rates[S.material];
          const sr = rateAt(S.sides === "double" ? src.double : src.single, sIdx) || 0;
          base = sheets * sr;
          lines.push([sheets + " full sheet" + (sheets === 1 ? "" : "s") + " × " + fmt(sr) + " (" + rate.label + " " + S.sides + (tierName ? " · " + tierName : "") + ")", base]);
        }
      } else if (p.pricing.type === "sqft") {
        const r = rateAt(S.sides === "double" ? rate.double : rate.single, ti) || 0;
        base = areaT * r;
        lines.push([areaT.toFixed(1) + " sqft × " + fmt(r) + " (" + rate.label + " " + S.sides + (tierName ? " · " + tierName : "") + ")", base]);
      } else if (p.pricing.type === "sqin") {
        const r = rateAt(rate.single, ti);
        base = S.w * S.h * S.qty * r;
        lines.push([(S.w * S.h * S.qty).toFixed(0) + " sq in × " + r.toFixed(3).replace(/^0/, "$0"), base]);
      }
      const A = CFG.addonPrices;
      let ad = 0;
      const addL = (l, a) => { lines.push([l, a]); ad += a; };
      if (S.grommets && has("grommets")) {
        const n = grommetPoints(S.w, S.h, S.grommetPreset, S.grommetSpacing).length * S.qty;
        if (p.freeFinishing) addL("Grommets × " + n + " — included", 0);
        else addL("Grommets × " + n, n * A.grommetEach);
      }
      if (S.stakes > 0 && has("stakes")) addL("Step stakes × " + S.stakes, S.stakes * A.stakeEach);
      if (S.gloss && has("gloss")) addL("Gloss laminate", areaT * A.glossPerSqft);
      if (S.contour && has("contour")) addL("Contour cutting (+10%)", Math.max(5, base * 0.10));
      if (S.drillHoles && has("drillHoles")) addL("Drill holes × " + 4 * S.qty, 4 * S.qty * A.drillHoleEach);
      if (S.roundCorners && has("roundCorners")) addL("Rounded corners", A.roundCornersFlat);
      if (has("hem") && S.hem) {
        const ln = (2 * (S.w + S.h) / 12) * S.qty;
        if (p.freeFinishing) addL("Welded hem — included", 0);
        else addL("Welded hem (" + ln.toFixed(0) + " lnft)", ln * A.hemPerLnft);
      }
      if (has("rope") && S.rope !== "none") {
        const e = S.rope === "topbottom" ? 2 : 1, ln = (S.w / 12) * e * S.qty;
        addL("Rope (" + ln.toFixed(0) + " lnft)", ln * A.ropePerLnft);
      }
      if (has("polePockets") && S.polePocket !== "none") {
        const v = ["left", "right", "leftright"].includes(S.polePocket);
        const e = ["topbottom", "leftright"].includes(S.polePocket) ? 2 : 1;
        const ln = ((v ? S.h : S.w) / 12) * e * S.qty;
        addL("Pole pockets " + S.polePocketSize + '" (' + ln.toFixed(0) + " lnft)", ln * A.polePocketPerLnft);
      }
      if (has("windSlits") && S.windSlits) {
        if (S.w >= 24 && S.h >= 24 && S.w <= 120 && S.h <= 120) addL("Wind slits × " + S.qty, S.qty * A.windSlitsFlat);
        else warnings.push('Wind slits need banners over 24"×24" and under 120"×120".');
      }
      if (p.maxSize && (S.w > p.maxSize.w || S.h > p.maxSize.h))
        warnings.push('Max size is ' + p.maxSize.w + '" × ' + p.maxSize.h + '".');
      const nest = p.pricing.sheetW ? nestFit(S.w, S.h) : null;
      if (nest) nest.sheets = nest.fit ? Math.ceil(S.qty / nest.fit) : 0;
      return { lines, warnings, total: base + ad, nest };
    }
    /* per-sign price in cents + the total the customer actually pays —
       identical formula in the cart transform function */
    function canonical(c) {
      const totalCents = Math.round(c.total * 100);
      const unitCents = Math.ceil(totalCents / S.qty);
      return { unitCents, total: (unitCents * S.qty) / 100 };
    }

    /* ---------- preview ---------- */
    function previewSVG() {
      const maxW = 520, maxH = 330;
      const sc = Math.min(maxW / S.w, maxH / S.h) || 1;
      const W = Math.max(40, S.w * sc), H = Math.max(30, S.h * sc), pad = 44;
      let inner;
      if (S.artDataUrl) {
        inner = '<image href="' + esc(S.artDataUrl) + '" x="0" y="0" width="' + W + '" height="' + H + '" preserveAspectRatio="xMidYMid slice"/>';
      } else if (S.artName) {
        inner = '<rect width="' + W + '" height="' + H + '" fill="#eeeef1"/>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 - 6) + '" text-anchor="middle" fill="#3d8a4d" font-size="13" font-weight="700">✓ FILE ATTACHED</text>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 + 14) + '" text-anchor="middle" fill="#9a9aa3" font-size="13" font-weight="700">PREVIEW NOT AVAILABLE FOR THIS FORMAT</text>';
      } else {
        inner = '<rect width="' + W + '" height="' + H + '" fill="#eeeef1"/>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 - 6) + '" text-anchor="middle" fill="#9a9aa3" font-size="13" font-weight="700">UPLOAD YOUR ARTWORK</text>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 + 14) + '" text-anchor="middle" fill="#9a9aa3" font-size="13" font-weight="700">TO PREVIEW YOUR SIGN</text>';
      }
      let gr = "";
      if (S.grommets && has("grommets")) {
        gr = grommetPoints(S.w, S.h, S.grommetPreset, S.grommetSpacing)
          .map(([x, y]) => '<circle cx="' + x * sc + '" cy="' + y * sc + '" r="6" fill="#fff" stroke="#555" stroke-width="2.5"/>')
          .join("");
      }
      const m = 0.25 * sc;
      return '<svg width="' + (W + pad * 2) + '" height="' + (H + pad * 2) + '" viewBox="0 0 ' + (W + pad * 2) + " " + (H + pad * 2) + '" style="max-width:100%">' +
        '<defs><clipPath id="tcclip"><rect width="' + W + '" height="' + H + '"/></clipPath></defs>' +
        '<g transform="translate(' + pad + "," + pad + ')">' +
        '<rect width="' + W + '" height="' + H + '" fill="#fff"/>' +
        '<g clip-path="url(#tcclip)">' + inner + "</g>" +
        '<rect x="' + m + '" y="' + m + '" width="' + (W - 2 * m) + '" height="' + (H - 2 * m) + '" fill="none" stroke="#d81b28" stroke-width="1" stroke-dasharray="5 4" opacity=".55"/>' + gr +
        '<rect width="' + W + '" height="' + H + '" fill="none" stroke="#1a1a1e" stroke-width="1.5"/>' +
        '<text x="' + W / 2 + '" y="-14" text-anchor="middle" fill="#55555c" font-size="12" font-weight="800">' + S.w + '"</text>' +
        '<text x="-18" y="' + H / 2 + '" text-anchor="middle" fill="#55555c" font-size="12" font-weight="800" transform="rotate(-90 -18 ' + H / 2 + ')">' + S.h + '"</text>' +
        "</g></svg>";
    }
    function nestHTML(nest) {
      if (!nest || !nest.fit) return "";
      let extra = "";
      if (inSheetMode()) {
        const slots = nest.sheets * nest.fit - S.qty;
        extra = slots > 0
          ? " Priced by the full sheet — <b>" + slots + " more sign" + (slots === 1 ? "" : "s") + "</b> fit at no extra cost."
          : " Priced by the full sheet.";
      }
      return '<div class="tc-nest"><b>' + nest.fit + " signs</b> fit per " + p.pricing.sheetW + '"×' + p.pricing.sheetH +
        '" production sheet — your ' + S.qty + " sign" + (S.qty === 1 ? "" : "s") + " run on <b>" + nest.sheets +
        " sheet" + (nest.sheets === 1 ? "" : "s") + "</b>." + extra + "</div>";
    }
    /* S365-style sheet layout: the 4x8 sheet with your signs nested on it */
    function sheetSVG(nest) {
      if (!inSheetMode() || !nest || !nest.fit) return "";
      const SW = p.pricing.sheetW, SH = p.pricing.sheetH;
      const scale = 220 / SW;
      const W = SW * scale, H = SH * scale, pad = 14;
      const cw = (nest.rot ? S.h : S.w) * scale;
      const ch = (nest.rot ? S.w : S.h) * scale;
      const onSheet1 = Math.min(S.qty, nest.fit);
      let cells = "";
      let i = 0;
      for (let r = 0; r < nest.rows; r++) {
        for (let c = 0; c < nest.cols; c++) {
          const filled = i < onSheet1;
          i++;
          cells += '<rect x="' + (c * cw + 2) + '" y="' + (r * ch + 2) + '" width="' + (cw - 4) + '" height="' + (ch - 4) +
            '" fill="' + (filled ? "#ffffff" : "#f0f0f3") + '" stroke="#2743c9" stroke-width="1.2" stroke-dasharray="5 3" opacity="' + (filled ? "1" : ".4") + '"/>';
        }
      }
      return '<div class="tc-sheet-wrap" style="text-align:center;margin-top:14px">' +
        '<svg width="' + (W + pad * 2) + '" height="' + (H + pad * 2) + '" viewBox="0 0 ' + (W + pad * 2) + " " + (H + pad * 2) + '" style="max-width:100%">' +
        '<g transform="translate(' + pad + "," + pad + ')">' +
        '<rect width="' + W + '" height="' + H + '" fill="#fbfbfc" stroke="#1a1a1e" stroke-width="1.5"/>' + cells +
        "</g></svg>" +
        '<div style="font-size:12px;color:#6f6f78;font-weight:700;margin-top:4px">Sheet #1 of ' + nest.sheets + " — " + SW + '" × ' + SH + '"' +
        (nest.rot ? " · signs rotated 90°" : "") + " · " + nest.fit + " per sheet</div></div>";
    }
    function renderPreview() {
      const c = calc();
      $("#tc-preview").innerHTML = previewSVG() + sheetSVG(c.nest) + nestHTML(c.nest);
    }

    /* ---------- pricing info popup (S365-style) ---------- */
    function unitSuffix() { return p.pricing.type === "sheet" ? " /sheet" : p.pricing.type === "sqin" ? " /sq in" : " /sqft"; }
    function fmtRate(r) { return (r < 0.1 ? "$" + r.toFixed(3) : fmt(r)) + unitSuffix(); }
    function pricingModal() {
      const pr = p.pricing;
      const nTiers = pr.tiers && pr.tiers.length > 1 ? pr.tiers.length : 1;
      const heads = nTiers > 1 ? pr.tierNames : ["Trade Price"];
      let rows = "";
      matKeys.forEach((k) => {
        const r = pr.rates[k];
        const cells = (val) => {
          let tds = "";
          for (let i = 0; i < nTiers; i++) tds += "<td>" + fmtRate(rateAt(val, i)) + "</td>";
          return tds;
        };
        rows += "<tr><td>" + esc(r.label) + " Single-Sided</td>" + cells(r.single) + "</tr>";
        if (r.double) rows += "<tr><td>" + esc(r.label) + " Double-Sided</td>" + cells(r.double) + "</tr>";
      });
      const A = CFG.addonPrices;
      const addons = [];
      if (has("grommets")) addons.push(["Grommets", p.freeFinishing ? "Included free" : fmt(A.grommetEach) + " each"]);
      if (has("hem")) addons.push(["Welded Hem", p.freeFinishing ? "Included free" : fmt(A.hemPerLnft) + " per linear foot"]);
      if (has("stakes")) addons.push(["Step Stakes", fmt(A.stakeEach) + " each"]);
      if (has("gloss")) addons.push(["Gloss Finish", fmt(A.glossPerSqft) + " per sqft"]);
      if (has("contour")) addons.push(["Contour Cut", "+10% of material price (min $5)"]);
      if (has("drillHoles")) addons.push(["Drill Holes", fmt(A.drillHoleEach) + " each (4 per sign)"]);
      if (has("roundCorners")) addons.push(["Rounded Corners", fmt(A.roundCornersFlat) + " flat"]);
      if (has("rope")) addons.push(["Rope", fmt(A.ropePerLnft) + " per linear foot"]);
      if (has("polePockets")) addons.push(["Pole Pockets", fmt(A.polePocketPerLnft) + " per linear foot"]);
      if (has("windSlits")) addons.push(["Wind Slits", fmt(A.windSlitsFlat) + " per banner"]);
      if (hasDualMode) {
        matKeys.forEach((k) => {
          const sr = p.pricing.sheet.rates[k];
          if (!sr) return;
          rows += "<tr><td>" + esc(sr.label || k) + ' Full Sheet SS</td><td colspan="' + nTiers + '">' + fmt(sr.single) + " per 48\"×96\" sheet</td></tr>";
          if (sr.double) rows += "<tr><td>" + esc(sr.label || k) + ' Full Sheet DS</td><td colspan="' + nTiers + '">' + fmt(sr.double) + " per 48\"×96\" sheet</td></tr>";
        });
      }
      let addonRows = addons.map((a) => "<tr><td>" + a[0] + '</td><td colspan="' + nTiers + '">' + a[1] + "</td></tr>").join("");
      const minNote = p.minSqftPerSign ? '<p class="tcp-note">Minimum billable size: ' + p.minSqftPerSign + " sqft per sign.</p>" : "";
      const sheetNote = isSheetProduct
        ? '<p class="tcp-note">Sold by the full ' + pr.sheetW + '"×' + pr.sheetH + '" sheet — your signs nest onto sheets automatically (layout shown on the left), and any leftover space on a committed sheet fits extra signs free.</p>'
        : "";
      const tierNote = nTiers > 1 && pr.tierBy === "sheets"
        ? '<p class="tcp-note">Volume tiers apply automatically by production sheets used (' + pr.sheetW + '"×' + pr.sheetH + '" sheets — the configurator shows your nesting live).</p>'
        : nTiers > 1 ? '<p class="tcp-note">Volume tiers apply automatically by total square footage in the order line.</p>' : "";
      return '<div class="tcp-backdrop" id="tcp-backdrop"><div class="tcp-modal">' +
        '<div class="tcp-head"><h3>Trade Pricing — ' + esc(p.name) + '</h3><button type="button" class="tcp-x" aria-label="Close">&times;</button></div>' +
        '<div class="tcp-body"><table class="tcp-table"><thead><tr><th></th>' +
        heads.map((h) => "<th>" + esc(h) + "</th>").join("") +
        "</tr></thead><tbody>" + rows + addonRows + "</tbody></table>" +
        sheetNote + tierNote + minNote +
        '<p class="tcp-note">✓ ' + esc(p.production) + " · Blind shipped · Order by 6 PM MST</p>" +
        "</div></div></div>";
    }
    function openPricing() {
      closePricing();
      const wrap = document.createElement("div");
      wrap.innerHTML = pricingModal();
      const el = wrap.firstChild;
      document.body.appendChild(el);
      document.body.style.overflow = "hidden";
      el.addEventListener("click", (e) => { if (e.target === el) closePricing(); });
      el.querySelector(".tcp-x").addEventListener("click", closePricing);
    }
    function closePricing() {
      const el = document.getElementById("tcp-backdrop");
      if (el) { el.remove(); document.body.style.overflow = ""; }
    }
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closePricing(); });
    (function injectPricingLink() {
      const css = document.createElement("style");
      css.textContent =
        ".tc-pricing-link{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:#d81b28;" +
        "font-weight:800;font-size:12.5px;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;padding:2px 0;" +
        "border-bottom:2px solid #d81b28;margin:2px 0 10px}" +
        ".tc-pricing-link:hover{color:#ff2d3d;border-color:#ff2d3d}" +
        ".tcp-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:9998;display:flex;align-items:center;justify-content:center;padding:20px}" +
        ".tcp-modal{background:#fff;border-radius:12px;max-width:720px;width:100%;max-height:88vh;overflow:auto;box-shadow:0 24px 80px rgba(0,0,0,.4)}" +
        ".tcp-head{position:sticky;top:0;background:#0d0d0f;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-bottom:3px solid #d81b28}" +
        ".tcp-head h3{margin:0;font-size:15px;font-weight:800;letter-spacing:.06em;text-transform:uppercase}" +
        ".tcp-x{background:none;border:none;color:#fff;font-size:24px;cursor:pointer;line-height:1;padding:2px 6px}" +
        ".tcp-x:hover{color:#ff2d3d}" +
        ".tcp-body{padding:20px 24px 24px}" +
        ".tcp-table{width:100%;border-collapse:collapse;font-size:13.5px}" +
        ".tcp-table th{text-align:left;font-size:11.5px;letter-spacing:.06em;text-transform:uppercase;color:#d81b28;padding:8px 10px;border-bottom:2px solid #0d0d0f}" +
        ".tcp-table td{padding:8px 10px;border-bottom:1px solid #ececf0;color:#3d3d44}" +
        ".tcp-table td:first-child{font-weight:700;color:#17171b}" +
        ".tcp-note{font-size:12.5px;color:#6f6f78;margin:12px 0 0;line-height:1.5}";
      document.head.appendChild(css);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "tc-pricing-link";
      btn.innerHTML = "Pricing &amp; Options";
      btn.addEventListener("click", openPricing);
      const title = $(".tc-title");
      if (title && title.parentNode) title.parentNode.insertBefore(btn, title.nextSibling);
    })();

    /* ---------- options UI ---------- */
    function seg(label, on, act, cls, dis) {
      return '<button type="button" class="' + (on ? (cls || "on") : "") + '" ' + (dis ? "disabled" : "") + " " + act + ">" + esc(label) + "</button>";
    }
    function tgl(label, key, on, hint) {
      return '<div class="tc-row"><div class="tc-label"><span>' + label + "</span>" + (hint ? '<span class="tc-hint">' + hint + "</span>" : "") + "</div>" +
        '<div class="tc-seg">' + seg("No", !on, 'data-b="' + key + '" data-v="0"') + seg("Yes", on, 'data-b="' + key + '" data-v="1"', "on-red") + "</div></div>";
    }
    function renderOptions() {
      const rate = p.pricing.rates[S.material];
      const rows = [];
      rows.push('<div class="tc-row"><div class="tc-label"><span>Size</span><span class="tc-hint">max ' + p.maxSize.w + '"×' + p.maxSize.h + '"</span></div>' +
        '<div class="tc-inputs"><input type="number" data-n="w" value="' + S.w + '" min="1"> <b>×</b> <input type="number" data-n="h" value="' + S.h + '" min="1"> <span>inches</span></div></div>');
      rows.push('<div class="tc-row"><div class="tc-label"><span>Quantity</span></div><div class="tc-inputs"><input type="number" data-n="qty" value="' + S.qty + '" min="1"></div></div>');
      if (hasDualMode) {
        rows.unshift('<div class="tc-row"><div class="tc-label"><span>Buy By</span><span class="tc-hint">sheets = best rate</span></div><div class="tc-seg">' +
          seg("Full Sheet", S.buyMode === "sheet", 'data-o="buyMode" data-v="sheet"', "on-red") +
          seg("Custom Size", S.buyMode === "custom", 'data-o="buyMode" data-v="custom"') + "</div></div>");
      }
      if (matKeys.length > 1) {
        rows.push('<div class="tc-row"><div class="tc-label"><span>Material</span></div><div class="tc-seg">' +
          matKeys.map((k, i) => seg(p.pricing.rates[k].label, S.material === k, 'data-m="' + i + '"')).join("") + "</div></div>");
      }
      if (has("doubleSided")) {
        const ds = !!rate.double;
        rows.push('<div class="tc-row"><div class="tc-label"><span>Print Sides</span></div><div class="tc-seg">' +
          seg("Single", S.sides === "single", 'data-o="sides" data-v="single"') +
          seg("Double", S.sides === "double", 'data-o="sides" data-v="double"', "on-red", !ds) + "</div>" +
          (ds ? "" : '<div class="tc-warn">Double-sided isn\'t available on ' + esc(rate.label) + " — change the material to enable it.</div>") + "</div>");
      }
      if (has("grommets")) {
        rows.push('<div class="tc-row"><div class="tc-label"><span>Grommets</span><span class="tc-hint">' + (p.freeFinishing ? "included free" : fmt(CFG.addonPrices.grommetEach) + " each") + "</span></div>" +
          '<div class="tc-seg">' + seg("No", !S.grommets, 'data-b="grommets" data-v="0"') + seg("Yes", S.grommets, 'data-b="grommets" data-v="1"', "on-red") + "</div>" +
          (S.grommets ? '<div class="tc-selrow"><select data-s="grommetPreset">' +
            CFG.grommetPresets.map((g) => '<option value="' + g.id + '"' + (S.grommetPreset === g.id ? " selected" : "") + ">" + g.name + "</option>").join("") + "</select>" +
            (S.grommetPreset === "corners" ? "" : '<select data-s="grommetSpacing" data-num="1">' +
              CFG.grommetSpacings.map((g) => '<option value="' + g.inches + '"' + (S.grommetSpacing === g.inches ? " selected" : "") + ">" + g.name + "</option>").join("") + "</select>") +
            "</div>" : "") + "</div>");
      }
      if (has("hem")) rows.push(tgl("Welded Hem", "hem", S.hem, p.freeFinishing ? "included free" : "recommended"));
      if (has("rope")) rows.push('<div class="tc-row"><div class="tc-label"><span>Rope</span></div><select data-s="rope">' +
        [["none", "None"], ["top", "Top Only"], ["bottom", "Bottom Only"], ["topbottom", "Top & Bottom"]]
          .map(([v, l]) => '<option value="' + v + '"' + (S.rope === v ? " selected" : "") + ">" + l + "</option>").join("") + "</select></div>");
      if (has("polePockets")) rows.push('<div class="tc-row"><div class="tc-label"><span>Pole Pockets</span></div><div class="tc-selrow"><select data-s="polePocket">' +
        [["none", "None"], ["top", "Top Only"], ["bottom", "Bottom Only"], ["topbottom", "Top & Bottom"], ["left", "Left Only"], ["right", "Right Only"], ["leftright", "Left & Right"]]
          .map(([v, l]) => '<option value="' + v + '"' + (S.polePocket === v ? " selected" : "") + ">" + l + "</option>").join("") + "</select>" +
        (S.polePocket === "none" ? "" : '<select data-s="polePocketSize" style="max-width:90px">' +
          ["1", "2", "3", "4"].map((v) => '<option value="' + v + '"' + (S.polePocketSize === v ? " selected" : "") + ">" + v + '"</option>').join("") + "</select>") + "</div></div>");
      if (has("windSlits")) rows.push(tgl("Wind Slits", "windSlits", S.windSlits, 'banners 24"–120"'));
      if (has("stakes")) rows.push('<div class="tc-row"><div class="tc-label"><span>Step Stakes</span><span class="tc-hint">' + fmt(CFG.addonPrices.stakeEach) + " each</span></div>" +
        '<div class="tc-inputs"><input type="number" data-n="stakes" value="' + S.stakes + '" min="0"></div></div>');
      if (has("gloss")) rows.push(tgl("Gloss Finish", "gloss", S.gloss, "+" + fmt(CFG.addonPrices.glossPerSqft) + "/sqft"));
      if (has("contour")) rows.push(tgl("Contour Cut", "contour", S.contour, "+10% \u00b7 cut to any shape"));
      if (has("drillHoles")) rows.push(tgl("Drill Holes (4)", "drillHoles", S.drillHoles, "for standoffs"));
      if (has("roundCorners")) rows.push(tgl("Rounded Corners", "roundCorners", S.roundCorners, ""));
      $("#tc-options").innerHTML = rows.join("");
      renderPrice();
    }
    function renderPrice() {
      const c = calc();
      const k = canonical(c);
      $("#tc-price").innerHTML =
        c.lines.map(([l, v]) => '<div class="tc-line"><span>' + l + "</span><span>" + fmt(v) + "</span></div>").join("") +
        c.warnings.map((w) => '<div class="tc-warn">⚠ ' + w + "</div>").join("") +
        '<div class="tc-line"><span>Per sign × ' + S.qty + "</span><span>" + fmt(k.unitCents / 100) + "</span></div>" +
        '<div class="tc-line tc-total"><span>Total</span><span>' + fmt(k.total) + "</span></div>" +
        '<div class="tc-time">✓ ' + p.production + " · Blind Shipped</div>";
      const btn = $("#tc-add");
      btn.disabled = c.total <= 0 || c.warnings.length > 0;
      btn.textContent = "Add to Cart — " + fmt(k.total);
      renderPreview();
    }

    /* ---------- events (delegated) ---------- */
    rootEl.addEventListener("click", (e) => {
      const b = e.target.closest("[data-b],[data-o],[data-m]");
      if (!b || b.disabled) return;
      if (b.dataset.m !== undefined) {
        S.material = matKeys[+b.dataset.m];
        const r = p.pricing.rates[S.material];
        if (S.sides === "double" && !r.double) S.sides = "single";
      } else if (b.dataset.o) S[b.dataset.o] = b.dataset.v;
      else if (b.dataset.b) S[b.dataset.b] = b.dataset.v === "1";
      renderOptions();
    });
    rootEl.addEventListener("change", (e) => {
      const s = e.target.closest("[data-s]");
      if (!s) return;
      S[s.dataset.s] = s.dataset.num ? +s.value : s.value;
      renderOptions();
    });
    rootEl.addEventListener("input", (e) => {
      const n = e.target.closest("[data-n]");
      if (!n) return;
      const k = n.dataset.n, min = k === "stakes" ? 0 : 1;
      let v = Math.max(min, parseFloat(n.value) || min);
      /* qty and stakes must be whole numbers — Shopify truncates fractional cart quantities */
      if (k === "qty" || k === "stakes") { v = Math.round(v); n.value = v; }
      if ((k === "w" || k === "h") && p.maxSize) {
        const mx = k === "w" ? p.maxSize.w : p.maxSize.h;
        if (v > mx) { v = mx; n.value = v; }
        if (p.minSize) {
          const mn = k === "w" ? p.minSize.w : p.minSize.h;
          if (v < mn) { v = mn; n.value = v; }
        }
      }
      S[k] = v;
      renderPrice();
    });

    /* ---------- artwork upload: print-ready formats only ---------- */
    const artInput = $("#tc-art");
    artInput.addEventListener("change", () => {
      const f = artInput.files[0];
      if (!f) return;
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ART_ALLOWED.includes(ext)) {
        artInput.value = "";
        S.artName = null; S.artDataUrl = null; S.artUrl = null;
        $("#tc-upload-label").innerHTML =
          '<span class="tc-warn">✕ We can\'t accept .' + esc(ext) + " working files</span> — " +
          "export a print-ready <b>PDF</b> (outline fonts, flatten layers) and upload that instead. " +
          "Accepted: PDF, TIFF, JPG, PNG, EPS.";
        renderPreview();
        return;
      }
      if (f.size > ART_MAX_BYTES) {
        artInput.value = "";
        S.artName = null; S.artDataUrl = null; S.artUrl = null;
        $("#tc-upload-label").innerHTML =
          '<span class="tc-warn">✕ ' + esc(f.name) + " is over 20MB</span> — " +
          "place your order, then email the file with your order number.";
        renderPreview();
        return;
      }
      S.artUrl = null; // a fresh upload replaces any past-order artwork
      S.artName = f.name;
      if (ART_PREVIEWABLE.includes(ext)) {
        $("#tc-upload-label").innerHTML = '<span class="tc-ok">✓ ' + esc(f.name) + "</span> — click to replace";
        const rd = new FileReader();
        rd.onload = () => { S.artDataUrl = rd.result; renderPreview(); };
        rd.readAsDataURL(f);
      } else {
        S.artDataUrl = null;
        $("#tc-upload-label").innerHTML = '<span class="tc-ok">✓ ' + esc(f.name) + " attached</span> — no browser preview for ." + esc(ext) + " · click to replace";
        renderPreview();
      }
    });

    /* ---------- reuse artwork from a past order (?art=<upload URL>) ---------- */
    (function preloadArt() {
      let art = null;
      try { art = new URLSearchParams(window.location.search).get("art"); } catch (e) { return; }
      if (!art) return;
      const ok = art.indexOf("/") === 0 ||
        /^https:\/\/([a-z0-9-]+\.)*(shopify\.com|shopifycdn\.net|fastcampaignsigns\.com)\//i.test(art);
      if (!ok) return;
      S.artUrl = art;
      S.artName = decodeURIComponent((art.split("/").pop() || "artwork").split("?")[0]);
      const ext = (S.artName.split(".").pop() || "").toLowerCase();
      if (ART_PREVIEWABLE.includes(ext)) S.artDataUrl = art;
      $("#tc-upload-label").innerHTML =
        '<span class="tc-ok">✓ ' + esc(S.artName) + "</span> — artwork from a previous order · click to replace";
    })();

    /* ---------- add to cart (real quantity + _tc config) ---------- */
    function tcConfig() {
      const cfg = { w: S.w, h: S.h, m: S.material, ds: S.sides === "double" ? 1 : 0 };
      if (S.grommets && has("grommets")) { cfg.gr = 1; cfg.gp = S.grommetPreset; cfg.gs = S.grommetSpacing; }
      if (S.stakes > 0 && has("stakes")) cfg.st = S.stakes;
      if (S.gloss && has("gloss")) cfg.gl = 1;
      if (S.contour && has("contour")) cfg.ct = 1;
      if (S.drillHoles && has("drillHoles")) cfg.dh = 1;
      if (S.roundCorners && has("roundCorners")) cfg.rc = 1;
      if (has("hem") && S.hem) cfg.hm = 1;
      if (has("rope") && S.rope !== "none") cfg.rp = S.rope;
      if (has("polePockets") && S.polePocket !== "none") { cfg.pp = S.polePocket; cfg.pps = S.polePocketSize; }
      if (has("windSlits") && S.windSlits) cfg.ws = 1;
      if (hasDualMode && S.buyMode === "sheet") cfg.md = 1;
      return cfg;
    }
    function propInputs() {
      const rate = p.pricing.rates[S.material];
      const props = {
        "Size": S.w + '" × ' + S.h + '"',
        "Material": rate.label,
      };
      if (has("doubleSided")) props["Print Sides"] = S.sides === "double" ? "Double-Sided" : "Single-Sided";
      if (S.grommets && has("grommets")) {
        const gp = CFG.grommetPresets.find((g) => g.id === S.grommetPreset);
        props["Grommets"] = gp.name + (S.grommetPreset === "corners" ? "" : " · every " + S.grommetSpacing + '"');
      }
      if (S.stakes > 0 && has("stakes")) props["Step Stakes"] = String(S.stakes);
      if (S.gloss && has("gloss")) props["Gloss Finish"] = "Yes";
      if (S.contour && has("contour")) props["Contour Cut"] = "Yes";
      if (S.drillHoles && has("drillHoles")) props["Drill Holes"] = "4 per sign";
      if (S.roundCorners && has("roundCorners")) props["Rounded Corners"] = "Yes";
      if (has("hem")) props["Welded Hem"] = S.hem ? "Yes" : "No";
      if (has("rope") && S.rope !== "none") props["Rope"] = S.rope;
      if (has("polePockets") && S.polePocket !== "none") props["Pole Pockets"] = S.polePocket + " · " + S.polePocketSize + '"';
      if (S.windSlits && has("windSlits")) props["Wind Slits"] = "Yes";
      if (inSheetMode()) {
        const n = nestFit(S.w, S.h);
        if (n && n.fit) props["Sheets"] = String(Math.ceil(S.qty / n.fit)) + ' × 48"×96" full sheet' + (Math.ceil(S.qty / n.fit) === 1 ? "" : "s");
      }
      /* reused past-order artwork rides as a URL property when no new file is chosen */
      if (S.artUrl && (!artInput.files || !artInput.files.length)) props["Artwork"] = S.artUrl;
      props["_tc"] = JSON.stringify(tcConfig());
      return Object.entries(props)
        .map(([k, v]) => '<input type="hidden" name="properties[' + esc(k) + ']" value="' + esc(v) + '">')
        .join("");
    }
    $("#tc-add").addEventListener("click", () => {
      const c = calc();
      if (c.total <= 0 || c.warnings.length) return;
      const k = canonical(c);
      $("#tc-cents").value = S.qty;
      $("#tc-props").innerHTML = propInputs();
      const form = $("#tc-form");
      const fd = new FormData(form);
      const btn = $("#tc-add");
      btn.disabled = true;
      btn.textContent = "Adding…";
      fetch("/cart/add", { method: "POST", body: fd })
        .then((r) => {
          if (!r.ok) throw new Error("cart add failed: " + r.status);
          window.location.href = "/cart";
        })
        .catch((err) => {
          btn.disabled = false;
          btn.textContent = "Add to Cart — " + fmt(k.total);
          alert("Couldn't add to cart — " + err.message + ". Try again or contact us.");
        });
    });

    /* dual-mode products ask up front, like S365 */
    if (hasDualMode) {
      const bd = document.createElement("div");
      bd.className = "tcp-backdrop";
      bd.id = "tc-mode-ask";
      bd.innerHTML = '<div class="tcp-modal" style="max-width:440px;text-align:center">' +
        '<div class="tcp-head"><h3>How do you want to order?</h3></div>' +
        '<div class="tcp-body"><p style="font-size:14px;color:#3d3d44;margin:0 0 16px">Full sheets get the best rate — custom size bills only the square footage you use.</p>' +
        '<button type="button" id="tc-mode-sheet" style="display:block;width:100%;margin-bottom:8px;background:#d81b28;color:#fff;border:none;border-radius:8px;font-weight:800;font-size:14.5px;padding:14px;cursor:pointer">By the Full Sheet — best rate</button>' +
        '<button type="button" id="tc-mode-custom" style="display:block;width:100%;background:#0d0d0f;color:#fff;border:none;border-radius:8px;font-weight:800;font-size:14.5px;padding:14px;cursor:pointer">Custom Size</button></div></div>';
      document.body.appendChild(bd);
      document.body.style.overflow = "hidden";
      const pick = (mode) => { S.buyMode = mode; bd.remove(); document.body.style.overflow = ""; renderOptions(); };
      bd.querySelector("#tc-mode-sheet").addEventListener("click", () => pick("sheet"));
      bd.querySelector("#tc-mode-custom").addEventListener("click", () => pick("custom"));
      /* never dead-lock the page: backdrop click or Esc = default to sheet mode */
      bd.addEventListener("click", (e) => { if (e.target === bd) pick("sheet"); });
      document.addEventListener("keydown", function onEsc(e) {
        if (e.key === "Escape" && document.getElementById("tc-mode-ask")) { pick("sheet"); document.removeEventListener("keydown", onEsc); }
      });
    }

    renderOptions();
    renderPreview();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
